import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSettings } from '../../hooks/useSettings';
import {
    FiHome,
    FiFilePlus,
    FiFolder,
    FiUsers,
    FiBarChart2,
    FiLogOut,
    FiFileText,
    FiSettings,
    FiX,
} from 'react-icons/fi';

const Sidebar = ({ isOpen, onClose }) => {
    const { user, logout } = useAuth();
    const { t } = useLanguage();
    const { logo } = useSettings();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const menuItems = [
        { path: '/dashboard', icon: FiHome, label: t('sidebar.dashboard'), description: t('sidebar.desc.dashboard') },
        { path: '/create-certificate', icon: FiFilePlus, label: t('sidebar.createCertificate'), description: t('sidebar.desc.createCertificate') },
        { path: '/certificate-history', icon: FiFolder, label: t('sidebar.certificateHistory'), description: t('sidebar.desc.certificateHistory') },
        { path: '/reports', icon: FiBarChart2, label: t('sidebar.reports'), description: t('sidebar.desc.reports') },
    ];

    const adminMenuItems = [
        { path: '/staff', icon: FiUsers, label: t('sidebar.staffManagement'), description: t('sidebar.desc.staffManagement') },
        { path: '/settings', icon: FiSettings, label: t('sidebar.settings'), description: t('sidebar.desc.settings') },
    ];

    const MenuItem = ({ item }) => (
        <NavLink
            to={item.path}
            onClick={onClose}
            className={({ isActive }) =>
                `group flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                        : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                }`
            }
        >
            <item.icon className={`w-5 h-5 flex-shrink-0`} />
            <div className="ml-3 flex-1">
                <div className="text-sm font-medium">{item.label}</div>
                <div className={`text-xs ${window.location.pathname === item.path ? 'text-blue-100' : 'text-gray-400 group-hover:text-blue-500'}`}>
                    {item.description}
                </div>
            </div>
        </NavLink>
    );

    return (
        <div
                className={`fixed left-0 top-0 h-full bg-gradient-to-b from-slate-50 to-white shadow-xl transition-transform duration-300 z-30 border-r border-gray-100 lg:translate-x-0 ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                } w-64`}
            >
                {/* Logo & Brand */}
                <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {logo ? (
                                <img src={logo} alt="Logo" className="w-10 h-10 object-contain rounded-lg" />
                            ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <FiFileText className="w-6 h-6 text-white" />
                                </div>
                            )}
                            <div>
                                <h1 className="text-lg font-bold text-gray-800">
                                    {t('sidebar.brandName')}
                                </h1>
                                <p className="text-xs text-gray-500">{t('sidebar.brandSub')}</p>
                            </div>
                        </div>
                        {/* Close button for mobile */}
                        <button
                            onClick={onClose}
                            className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* User Info */}
                <div className="px-4 py-3 mx-4 mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow">
                            {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                                {user?.name}
                            </p>
                            <p className="text-xs text-gray-500">
                                {t(user?.role === 'admin' ? 'header.administrator' : 'header.staff')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">
                        {t('sidebar.mainMenu')}
                    </div>
                    {menuItems.map((item) => (
                        <MenuItem key={item.path} item={item} />
                    ))}

                    {user?.role === 'admin' && (
                        <>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mt-6 mb-2">
                                {t('sidebar.administration')}
                            </div>
                            {adminMenuItems.map((item) => (
                                <MenuItem key={item.path} item={item} />
                            ))}
                        </>
                    )}
                </nav>

                {/* Logout Button */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white">
                    <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors group"
                    >
                        <FiLogOut className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
                        <span className="ml-3 text-sm font-medium">{t('login.logout')}</span>
                    </button>
                </div>
            </div>
    );
};

export default Sidebar;